import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";
import { MapPin, Phone, Clock, ArrowUpRight, Send } from "lucide-react";

const ContactPage = () => {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 5 || form.name.length < 2 || !form.email) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      if (data.warning) toast.warning(data.warning);
      else toast.success("Votre demande a bien été envoyée. Nous revenons vers vous sous 24h.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={ref} data-testid="contact-page">
      <section className="bg-teal-dark text-white pt-24 pb-16 md:pt-32 md:pb-20 grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 reveal">
          <p className="eyebrow text-teal-l3">Contact</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[1.02] font-light max-w-4xl">
            Parlons de votre <em className="text-teal-l3">projet</em>.
          </h1>
          <p className="mt-6 max-w-2xl text-teal-l3 text-lg">
            Un premier échange sans engagement pour comprendre vos besoins et vous proposer
            l'accompagnement le plus adapté.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow text-teal-accent">Coordonnées</p>
            <h2 className="mt-4 font-serif text-4xl text-teal-dark">Cabinet Ducrot &amp; Associés</h2>

            <ul className="mt-10 space-y-6">
              <li className="flex gap-4">
                <span className="bg-teal-l1 p-3"><MapPin size={20} className="text-teal-dark" /></span>
                <div>
                  <p className="eyebrow text-ink-500">Adresse</p>
                  <p className="text-ink-800 mt-1">4 Allée du Lauzon<br />84110 Vaison-la-Romaine</p>
                  <p className="text-ink-500 text-sm mt-1">Bureau secondaire à Lunel</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="bg-teal-l1 p-3"><Phone size={20} className="text-teal-dark" /></span>
                <div>
                  <p className="eyebrow text-ink-500">Téléphone</p>
                  <a href="tel:+33490363000" className="text-ink-800 mt-1 block hover:text-teal-accent">04 90 36 30 00</a>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="bg-teal-l1 p-3"><Clock size={20} className="text-teal-dark" /></span>
                <div>
                  <p className="eyebrow text-ink-500">Horaires</p>
                  <p className="text-ink-800 mt-1">Lundi au vendredi<br />9h00 – 18h30</p>
                </div>
              </li>
            </ul>

            <div className="mt-12 border-t border-ink-200 pt-8">
              <p className="eyebrow text-teal-accent">Inscrit à l'Ordre</p>
              <p className="mt-2 text-ink-800">Ordre des Experts-Comptables<br />de Provence Alpes Côte d'Azur</p>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form onSubmit={submit} data-testid="contact-form" className="border border-ink-200 p-8 md:p-10 bg-white">
              <h3 className="font-serif text-3xl text-teal-dark">Envoyez-nous un message</h3>
              <p className="text-sm text-ink-500 mt-2">Réponse sous 24h ouvrées.</p>

              <div className="mt-8 grid md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="eyebrow text-ink-500 block mb-2">Nom complet *</span>
                  <input
                    data-testid="contact-name"
                    required minLength={2}
                    className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
                    value={form.name} onChange={change("name")}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink-500 block mb-2">Adresse email *</span>
                  <input
                    data-testid="contact-email"
                    type="email" required
                    className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
                    value={form.email} onChange={change("email")}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink-500 block mb-2">Téléphone</span>
                  <input
                    data-testid="contact-phone"
                    className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
                    value={form.phone} onChange={change("phone")}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink-500 block mb-2">Sujet</span>
                  <input
                    data-testid="contact-subject"
                    placeholder="Ex : Demande de devis comptabilité"
                    className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
                    value={form.subject} onChange={change("subject")}
                  />
                </label>
              </div>

              <label className="block mt-5">
                <span className="eyebrow text-ink-500 block mb-2">Votre message *</span>
                <textarea
                  data-testid="contact-message"
                  required minLength={5} rows={6}
                  className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20 resize-none"
                  value={form.message} onChange={change("message")}
                />
              </label>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary mt-6 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Envoi en cours…" : (<>Envoyer ma demande <Send size={16} /></>)}
              </button>

              <p className="mt-6 text-xs text-ink-500">
                Vos données restent confidentielles et ne seront utilisées que pour vous répondre.
              </p>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-teal-l1 py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow text-teal-accent mb-4">Localisation</p>
          <div className="border border-ink-200 bg-white overflow-hidden">
            <iframe
              title="Carte Cabinet Ducrot"
              src="https://www.openstreetmap.org/export/embed.html?bbox=5.0744%2C44.2361%2C5.0944%2C44.2461&layer=mapnik&marker=44.2411%2C5.0844"
              className="w-full h-[420px] border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
